import React, { useEffect, useState, useCallback } from "react";
import {
  Clock, CalendarDays, Users, Settings2, Plus, LogIn, LogOut, FileClock,
  CheckCircle2, ChevronLeft, ChevronRight, X, Home,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, Avatar, EmptyState } from "../components/ui";

const STATUS_TONE = {
  PRESENT: "bg-emerald-500", LATE: "bg-amber-500", HALF_DAY: "bg-sky-500", ABSENT: "bg-red-500",
  WEEKLY_OFF: "bg-slate-300 dark:bg-slate-600", HOLIDAY: "bg-violet-500", ON_LEAVE: "bg-indigo-500",
  WFH: "bg-teal-500",
};
const STATUS_LABEL = {
  PRESENT: "Present", LATE: "Late", HALF_DAY: "Half Day", ABSENT: "Absent",
  WEEKLY_OFF: "Weekly Off", HOLIDAY: "Holiday", ON_LEAVE: "On Leave", WFH: "WFH",
};
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const timeStr = (t) => (t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");

function MonthNav({ month, year, onChange }) {
  const prev = () => { const m = month === 1 ? 12 : month - 1; onChange(m, month === 1 ? year - 1 : year); };
  const next = () => { const m = month === 12 ? 1 : month + 1; onChange(m, month === 12 ? year + 1 : year); };
  return (
    <div className="flex items-center gap-2">
      <button onClick={prev} data-testid="month-prev" className="rounded-md border border-slate-200 p-1.5 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"><ChevronLeft size={16} /></button>
      <span className="min-w-[110px] text-center text-sm font-semibold text-slate-700 dark:text-slate-200">{MONTHS[month - 1]} {year}</span>
      <button onClick={next} data-testid="month-next" className="rounded-md border border-slate-200 p-1.5 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"><ChevronRight size={16} /></button>
    </div>
  );
}

function CalendarGrid({ records, month, year }) {
  const firstDow = new Date(year, month - 1, 1).getDay();
  return (
    <div data-testid="attendance-calendar">
      <div className="mb-1 grid grid-cols-7 gap-1.5 text-center text-[11px] font-semibold uppercase text-slate-400">
        {WEEKDAYS.map((w, i) => <div key={i}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: firstDow }).map((_, i) => <div key={`b${i}`} />)}
        {records.map((r) => (
          <div key={r.day} title={`${r.day} ${MONTHS[month - 1]} · ${STATUS_LABEL[r.status] || "—"}${r.workHours ? ` · ${r.workHours}h` : ""}`}
            data-testid={`day-${r.day}`}
            className="relative flex min-h-[58px] flex-col rounded-lg border border-slate-100 p-1.5 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{r.day}</span>
              {r.status && <span className={`h-2 w-2 rounded-full ${STATUS_TONE[r.status] || "bg-slate-200"}`} />}
            </div>
            {r.status && r.workHours > 0 && <span className="mt-auto font-mono text-[10px] text-slate-400">{r.workHours}h{r.otHours > 0 ? ` +${r.otHours}` : ""}</span>}
            {r.regularized && <span className="absolute bottom-0.5 right-1 text-[8px] font-bold text-accent">R</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCards({ s }) {
  const items = [["Present", s.present, "text-emerald-600"], ["Late", s.late, "text-amber-600"],
    ["Half Day", s.halfDay, "text-sky-600"], ["Absent", s.absent, "text-red-600"],
    ["On Leave", s.onLeave, "text-indigo-600"], ["Total Hrs", s.totalHours, "text-slate-700 dark:text-slate-200"],
    ["OT Hrs", s.otHours, "text-violet-600"], ["Payable Days", s.payableDays, "text-slate-700 dark:text-slate-200"]];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {items.map(([l, v, c]) => (
        <div key={l} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}</p>
          <p className={`font-mono text-xl font-bold ${c}`}>{v}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------------- My Attendance ---------------- */
function MyAttendance({ meta }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [regs, setRegs] = useState([]);
  const [regOpen, setRegOpen] = useState(false);
  const [reg, setReg] = useState({ date: "", checkIn: "09:00", checkOut: "18:00", reason: "" });
  const [wfhs, setWfhs] = useState([]);
  const [wfhOpen, setWfhOpen] = useState(false);
  const [wfh, setWfh] = useState({ startDate: "", endDate: "", reason: "" });

  const load = useCallback(() => api.get(`/attendance/me?month=${month}&year=${year}`).then(({ data }) => setData(data)), [month, year]);
  const loadRegs = useCallback(() => api.get("/attendance/regularizations?scope=me").then(({ data }) => setRegs(data)), []);
  const loadWfh = useCallback(() => api.get("/attendance/wfh?scope=me").then(({ data }) => setWfhs(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRegs(); loadWfh(); }, [loadRegs, loadWfh]);

  const punch = async () => {
    const send = async (location) => {
      try { const { data } = await api.post("/attendance/punch", { location }); alert(`Punched ${data.action}${location ? " (location captured)" : ""}`); load(); }
      catch (e) { alert(apiError(e.response?.data?.detail)); }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => send({ lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6), accuracy: Math.round(pos.coords.accuracy) }),
        () => send(null), { timeout: 5000, maximumAge: 60000 });
    } else send(null);
  };
  const submitWfh = async (e) => {
    e.preventDefault();
    try { await api.post("/attendance/wfh", wfh); alert("WFH request submitted for approval"); setWfhOpen(false); setWfh({ startDate: "", endDate: "", reason: "" }); loadWfh(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  const submitReg = async (e) => {
    e.preventDefault();
    try { await api.post("/attendance/regularize", reg); alert("Regularization submitted for approval"); setRegOpen(false); setReg({ date: "", checkIn: "09:00", checkOut: "18:00", reason: "" }); loadRegs(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  if (!data) return <Loading />;
  const today = data.today;
  const punchedIn = today?.checkIn && !today?.checkOut;
  const done = today?.checkIn && today?.checkOut;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-1" data-testid="punch-card">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Today · {meta.myShift?.name || "No shift"}</p>
          <div className="mt-2 flex items-baseline gap-2">
            <Clock size={22} className="text-accent" />
            <span className="text-2xl font-bold text-slate-800 dark:text-slate-100">{done ? "Completed" : punchedIn ? "On the clock" : "Not punched in"}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-lg bg-slate-50 p-2 dark:bg-slate-800/50"><p className="text-[10px] uppercase text-slate-400">In</p><p className="font-mono font-semibold">{timeStr(today?.checkIn)}</p></div>
            <div className="rounded-lg bg-slate-50 p-2 dark:bg-slate-800/50"><p className="text-[10px] uppercase text-slate-400">Out</p><p className="font-mono font-semibold">{timeStr(today?.checkOut)}</p></div>
          </div>
          {!done && <button onClick={punch} data-testid="punch-btn" className="btn-accent mt-4 w-full justify-center">{punchedIn ? <><LogOut size={16} /> Punch Out</> : <><LogIn size={16} /> Punch In</>}</button>}
          {done && <p className="mt-4 text-center text-sm text-emerald-600">✓ {today.workHours}h logged{today.otHours > 0 ? ` (+${today.otHours} OT)` : ""}</p>}
        </Card>
        <Card className="p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Monthly Summary</h3>
            <MonthNav month={month} year={year} onChange={(m, y) => { setMonth(m); setYear(y); }} />
          </div>
          <SummaryCards s={data.summary} />
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Attendance Calendar</h3>
        <CalendarGrid records={data.records} month={month} year={year} />
        <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-slate-500">
          {Object.entries(STATUS_LABEL).map(([k, l]) => <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${STATUS_TONE[k]}`} />{l}</span>)}
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200"><FileClock size={15} /> My Regularizations</h3>
          <button onClick={() => setRegOpen(true)} data-testid="regularize-btn" className="btn-outline !py-1.5 text-xs"><Plus size={14} /> Request</button>
        </div>
        {regs.length === 0 ? <EmptyState icon={FileClock} title="No regularization requests" /> : (
          <div className="overflow-x-auto"><table className="w-full" data-testid="regularizations-table">
            <thead><tr><th className="th">Date</th><th className="th">In</th><th className="th">Out</th><th className="th">Reason</th><th className="th">Status</th><th className="th">With</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {regs.map((r) => (
                <tr key={r.id}><td className="td">{new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="td font-mono">{timeStr(r.requestedCheckIn)}</td><td className="td font-mono">{timeStr(r.requestedCheckOut)}</td>
                  <td className="td max-w-[200px] truncate">{r.reason}</td>
                  <td className="td"><Badge value={r.flowStatus || r.status} tone={r.flowStatus === "APPROVED" ? "ok" : r.flowStatus === "REJECTED" ? "bad" : "warn"} /></td>
                  <td className="td text-slate-500">{r.currentApproverLabel || "—"}</td></tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200"><Home size={15} /> Work From Home</h3>
          <button onClick={() => setWfhOpen(true)} data-testid="wfh-btn" className="btn-outline !py-1.5 text-xs"><Plus size={14} /> Request WFH</button>
        </div>
        {wfhs.length === 0 ? <EmptyState icon={Home} title="No WFH requests" /> : (
          <div className="overflow-x-auto"><table className="w-full" data-testid="wfh-table">
            <thead><tr><th className="th">From</th><th className="th">To</th><th className="th">Days</th><th className="th">Reason</th><th className="th">Status</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {wfhs.map((r) => (
                <tr key={r.id}><td className="td">{new Date(r.startDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="td">{new Date(r.endDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="td font-mono">{r.days}</td>
                  <td className="td max-w-[200px] truncate">{r.reason || "—"}</td>
                  <td className="td"><Badge value={r.status} tone={r.status === "APPROVED" ? "ok" : r.status === "REJECTED" ? "bad" : "warn"} /></td></tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>

      <Modal open={wfhOpen} onClose={() => setWfhOpen(false)} title="Request Work From Home"
        footer={<><button onClick={() => setWfhOpen(false)} className="btn-outline">Cancel</button><button form="wfh-form" className="btn-accent">Submit</button></>}>
        <form id="wfh-form" onSubmit={submitWfh} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">From *</label><input required type="date" data-testid="wfh-from" value={wfh.startDate} onChange={(e) => setWfh({ ...wfh, startDate: e.target.value })} className="input" /></div>
            <div><label className="label">To</label><input type="date" data-testid="wfh-to" value={wfh.endDate} onChange={(e) => setWfh({ ...wfh, endDate: e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Reason *</label><textarea required data-testid="wfh-reason" value={wfh.reason} onChange={(e) => setWfh({ ...wfh, reason: e.target.value })} className="input h-20 py-2" /></div>
          <p className="text-[11px] text-slate-400">On approval, those days are marked WFH in your attendance (payable).</p>
        </form>
      </Modal>

      <Modal open={regOpen} onClose={() => setRegOpen(false)} title="Request Regularization"
        footer={<><button onClick={() => setRegOpen(false)} className="btn-outline">Cancel</button><button form="reg-form" className="btn-accent">Submit</button></>}>
        <form id="reg-form" onSubmit={submitReg} className="space-y-4">
          <div><label className="label">Date *</label><input required type="date" data-testid="reg-date" value={reg.date} onChange={(e) => setReg({ ...reg, date: e.target.value })} className="input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Check In</label><input type="time" data-testid="reg-in" value={reg.checkIn} onChange={(e) => setReg({ ...reg, checkIn: e.target.value })} className="input" /></div>
            <div><label className="label">Check Out</label><input type="time" data-testid="reg-out" value={reg.checkOut} onChange={(e) => setReg({ ...reg, checkOut: e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Reason *</label><textarea required data-testid="reg-reason" value={reg.reason} onChange={(e) => setReg({ ...reg, reason: e.target.value })} className="input h-20 py-2" /></div>
        </form>
      </Modal>
    </div>
  );
}

/* ---------------- Team Attendance ---------------- */
function EmployeeDrawer({ employeeId, name, month, year, canMark, onClose, onSaved }) {
  const [g, setG] = useState(null);
  const [mark, setMark] = useState({ date: "", checkIn: "09:00", checkOut: "18:00" });
  const load = useCallback(() => api.get(`/attendance/records?employeeId=${employeeId}&month=${month}&year=${year}`).then(({ data }) => setG(data)), [employeeId, month, year]);
  useEffect(() => { load(); }, [load]);
  const saveMark = async (e) => {
    e.preventDefault();
    try { await api.post("/attendance/mark", { employeeId, ...mark }); alert("Attendance marked"); setMark({ date: "", checkIn: "09:00", checkOut: "18:00" }); load(); onSaved?.(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title={`${name} · ${MONTHS[month - 1]} ${year}`}>
      {!g ? <Loading /> : (
        <div className="space-y-4" data-testid="employee-drawer">
          <SummaryCards s={g.summary} />
          <CalendarGrid records={g.records} month={month} year={year} />
          {canMark && (
            <form onSubmit={saveMark} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <p className="mb-2 text-xs font-semibold uppercase text-slate-400">Manual Mark / Correct</p>
              <div className="grid grid-cols-3 gap-2">
                <input required type="date" data-testid="mark-date" value={mark.date} onChange={(e) => setMark({ ...mark, date: e.target.value })} className="input" />
                <input type="time" data-testid="mark-in" value={mark.checkIn} onChange={(e) => setMark({ ...mark, checkIn: e.target.value })} className="input" />
                <input type="time" data-testid="mark-out" value={mark.checkOut} onChange={(e) => setMark({ ...mark, checkOut: e.target.value })} className="input" />
              </div>
              <button className="btn-accent mt-2 w-full justify-center text-sm" data-testid="mark-submit"><CheckCircle2 size={15} /> Save</button>
            </form>
          )}
        </div>
      )}
    </Modal>
  );
}

function TeamAttendance({ meta }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const load = useCallback(() => api.get(`/attendance/team?month=${month}&year=${year}`).then(({ data }) => setData(data)), [month, year]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><MonthNav month={month} year={year} onChange={(m, y) => { setMonth(m); setYear(y); }} /></div>
      {data.employees.length === 0 ? <EmptyState icon={Users} title="No team members" /> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full" data-testid="team-table">
            <thead><tr><th className="th">Employee</th><th className="th">Shift</th><th className="th">Present</th><th className="th">Late</th><th className="th">Half</th><th className="th">Absent</th><th className="th">OT Hrs</th><th className="th">Payable</th><th className="th"></th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.employees.map((e) => (
                <tr key={e.employeeId} data-testid={`team-row-${e.employeeId}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="td"><div className="flex items-center gap-2"><Avatar name={e.employeeName} size={28} /><span className="font-medium text-slate-800 dark:text-slate-100">{e.employeeName}</span></div></td>
                  <td className="td text-slate-500">{e.shift || "—"}</td>
                  <td className="td font-mono text-emerald-600">{e.summary.present}</td>
                  <td className="td font-mono text-amber-600">{e.summary.late}</td>
                  <td className="td font-mono text-sky-600">{e.summary.halfDay}</td>
                  <td className="td font-mono text-red-600">{e.summary.absent}</td>
                  <td className="td font-mono">{e.summary.otHours}</td>
                  <td className="td font-mono font-semibold">{e.summary.payableDays}</td>
                  <td className="td text-right"><button onClick={() => setDrawer(e)} data-testid={`team-view-${e.employeeId}`} className="btn-outline !px-3 !py-1 text-xs">View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></Card>
      )}
      {drawer && <EmployeeDrawer employeeId={drawer.employeeId} name={drawer.employeeName} month={month} year={year} canMark={meta.canManage} onClose={() => setDrawer(null)} onSaved={load} />}
    </div>
  );
}

/* ---------------- Shifts ---------------- */
function Shifts({ meta, reloadMeta }) {
  const [shifts, setShifts] = useState(meta.shifts);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", startTime: "09:00", endTime: "18:00", graceMins: 15, halfDayHours: 4.5, fullDayHours: 9, weeklyOff: [5, 6] });
  const [assign, setAssign] = useState({ employeeId: "", shiftId: "" });
  const load = useCallback(() => api.get("/attendance/meta").then(({ data }) => { setShifts(data.shifts); }), []);

  const openNew = () => { setEdit(null); setForm({ name: "", code: "", startTime: "09:00", endTime: "18:00", graceMins: 15, halfDayHours: 4.5, fullDayHours: 9, weeklyOff: [5, 6] }); setOpen(true); };
  const openEdit = (s) => { setEdit(s.id); setForm({ ...s, weeklyOff: s.weeklyOff || [] }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (edit) await api.put(`/attendance/shifts/${edit}`, form); else await api.post("/attendance/shifts", form);
      alert("Shift saved"); setOpen(false); load(); reloadMeta();
    } catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this shift?")) return; await api.delete(`/attendance/shifts/${id}`); load(); reloadMeta(); };
  const toggleDow = (d) => setForm((f) => ({ ...f, weeklyOff: f.weeklyOff.includes(d) ? f.weeklyOff.filter((x) => x !== d) : [...f.weeklyOff, d] }));
  const doAssign = async (e) => { e.preventDefault(); try { await api.post("/attendance/shifts/assign", assign); alert("Shift assigned"); setAssign({ employeeId: "", shiftId: "" }); reloadMeta(); } catch (err) { alert(apiError(err.response?.data?.detail)); } };

  return (
    <div className="space-y-5">
      <div className="flex justify-end"><button onClick={openNew} data-testid="new-shift-btn" className="btn-accent"><Plus size={16} /> New Shift</button></div>
      <div className="grid gap-4 md:grid-cols-2">
        {shifts.map((s) => (
          <Card key={s.id} className="p-5" data-testid={`shift-${s.id}`}>
            <div className="flex items-start justify-between">
              <div><h3 className="font-semibold text-slate-800 dark:text-slate-100">{s.name} <span className="text-xs text-slate-400">({s.code})</span></h3>
                <p className="text-sm text-slate-500">{s.startTime} – {s.endTime} · grace {s.graceMins}m</p>
                <p className="text-xs text-slate-400">Full day ≥ {s.fullDayHours}h · Half ≥ {s.halfDayHours}h · Off: {(s.weeklyOff || []).map((d) => WEEKDAYS[d]).join(",") || "none"}</p></div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(s)} data-testid={`edit-shift-${s.id}`} className="btn-outline !px-2.5 !py-1 text-xs">Edit</button>
                <button onClick={() => del(s.id)} className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10"><X size={13} /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Assign Shift to Employee</h3>
        <form onSubmit={doAssign} className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1"><label className="label">Employee</label>
            <select required data-testid="assign-emp" value={assign.employeeId} onChange={(e) => setAssign({ ...assign, employeeId: e.target.value })} className="input"><option value="">— Select —</option>{meta.employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select></div>
          <div className="min-w-[200px] flex-1"><label className="label">Shift</label>
            <select required data-testid="assign-shift" value={assign.shiftId} onChange={(e) => setAssign({ ...assign, shiftId: e.target.value })} className="input"><option value="">— Select —</option>{shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <button className="btn-accent" data-testid="assign-submit">Assign</button>
        </form>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Shift" : "New Shift"}
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="shift-form" className="btn-accent">Save</button></>}>
        <form id="shift-form" onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Name *</label><input required data-testid="shift-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></div>
            <div><label className="label">Code</label><input data-testid="shift-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Start</label><input type="time" data-testid="shift-start" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="input" /></div>
            <div><label className="label">End</label><input type="time" data-testid="shift-end" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="label">Grace (m)</label><input type="number" value={form.graceMins} onChange={(e) => setForm({ ...form, graceMins: +e.target.value })} className="input" /></div>
            <div><label className="label">Half ≥ (h)</label><input type="number" step="0.5" value={form.halfDayHours} onChange={(e) => setForm({ ...form, halfDayHours: +e.target.value })} className="input" /></div>
            <div><label className="label">Full ≥ (h)</label><input type="number" step="0.5" value={form.fullDayHours} onChange={(e) => setForm({ ...form, fullDayHours: +e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Weekly Off</label>
            <div className="flex gap-1.5">{WEEKDAYS.map((w, d) => (
              <button type="button" key={d} onClick={() => toggleDow(d)} className={`h-9 w-9 rounded-md border text-sm font-semibold ${form.weeklyOff.includes(d) ? "border-accent bg-accent text-white" : "border-slate-200 text-slate-500 dark:border-slate-700"}`}>{w}</button>
            ))}</div>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* ---------------- Roster ---------------- */
function Roster() {
  const monday = () => { const d = new Date(); const day = d.getDay() || 7; d.setDate(d.getDate() - day + 1); return d.toISOString().slice(0, 10); };
  const [start, setStart] = useState(monday());
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/attendance/roster?start=${start}`).then(({ data }) => setData(data)), [start]);
  useEffect(() => { load(); }, [load]);
  const shiftWeek = (dir) => { const d = new Date(start); d.setDate(d.getDate() + dir * 7); setStart(d.toISOString().slice(0, 10)); };
  const setCell = async (employeeId, date, shiftId) => {
    try { await api.put("/attendance/roster", { employeeId, date, shiftId: shiftId || null }); load(); }
    catch (e) { alert(apiError(e.response?.data?.detail)); }
  };
  if (!data) return <Loading />;
  const shiftCode = (id) => data.shifts.find((s) => s.id === id)?.code || "";
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Assign day-wise shifts. Blank cells use the employee's default shift.</p>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftWeek(-1)} data-testid="roster-prev" className="rounded-md border border-slate-200 p-1.5 dark:border-slate-700"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Week of {new Date(start).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
          <button onClick={() => shiftWeek(1)} data-testid="roster-next" className="rounded-md border border-slate-200 p-1.5 dark:border-slate-700"><ChevronRight size={16} /></button>
        </div>
      </div>
      <Card className="overflow-hidden"><div className="overflow-x-auto">
        <table className="w-full min-w-[900px]" data-testid="roster-table">
          <thead><tr><th className="th">Employee</th>{data.days.map((d) => <th key={d} className="th text-center">{new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit" })}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.employees.map((e) => (
              <tr key={e.id} data-testid={`roster-row-${e.id}`}>
                <td className="td"><span className="font-medium text-slate-800 dark:text-slate-100">{e.name}</span>
                  <p className="text-[10px] text-slate-400">default: {shiftCode(e.defaultShiftId) || "—"}</p></td>
                {data.days.map((d) => (
                  <td key={d} className="td text-center">
                    <select value={e.cells[d] || ""} onChange={(ev) => setCell(e.id, d, ev.target.value)}
                      data-testid={`roster-cell-${e.id}-${d}`}
                      className={`input !h-8 !w-24 !px-1 !text-xs ${e.cells[d] ? "!border-accent !text-accent" : ""}`}>
                      <option value="">default</option>
                      {data.shifts.map((s) => <option key={s.id} value={s.id}>{s.code || s.name}</option>)}
                    </select>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div></Card>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function Attendance() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("me");
  const loadMeta = useCallback(() => api.get("/attendance/meta").then(({ data }) => setMeta(data)), []);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  if (!meta) return <Loading />;

  const tabs = [
    ["me", "My Attendance", Clock, true],
    ["team", "Team", Users, meta.canManage],
    ["roster", "Roster", CalendarDays, meta.canAdmin],
    ["shifts", "Shifts", Settings2, meta.canAdmin],
  ].filter((t) => t[3]);

  return (
    <div>
      <PageHeader title="Time & Attendance" subtitle="Punch, shifts, regularization & team tracking" />
      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-800">
        {tabs.map(([k, l, Ic]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}><Ic size={15} />{l}</button>
        ))}
      </div>
      {tab === "me" && <MyAttendance meta={meta} />}
      {tab === "team" && meta.canManage && <TeamAttendance meta={meta} />}
      {tab === "roster" && meta.canAdmin && <Roster />}
      {tab === "shifts" && meta.canAdmin && <Shifts meta={meta} reloadMeta={loadMeta} />}
    </div>
  );
}
